import { apiClient, type Paginated } from '@/core/api';
import type { OrganizationDto, TimerNoteMode } from '@/features/auth/types';

const ORG_BASE = '/organizations/';

/** Editable org settings (mirrors OrganizationSerializer writable fields). */
export interface OrganizationUpdateInput {
  name: string;
  fiscal_year_start_month: number;
  /** Hex color string. Empty string clears it. */
  primary_color: string;
  /** Issuer details printed on the staff-invoice PDF. */
  legal_name: string;
  /** Local-language pair for the bilingual contractor invoice ("" clears). */
  legal_name_local?: string;
  address: string;
  address_local?: string;
  contact_email: string;
  /** Company registration / tax ID printed on invoices. */
  registration_number?: string;
  /** ISO 3166-1 alpha-2 country code. */
  country?: string;
  /** Leading segment of staff-invoice numbers (e.g. "INV"). */
  staff_invoice_prefix?: string;
  /** Leading segment of client-invoice numbers (e.g. "INV"). */
  client_invoice_prefix?: string;
  /** Default commission basis prefilled onto new deals (ADR-046). */
  default_commission_basis?: 'none' | 'first_client_invoice' | 'all_paid_invoices';
  /** Org switch — allow manual "Log time" (timer is always available). */
  manual_time_logging_enabled?: boolean;
  /** Worklog note on timer stop: skippable or required (BE-ADR-109). */
  timer_note_mode?: TimerNoteMode;
}

export async function getOrganization(id: number): Promise<OrganizationDto> {
  const { data } = await apiClient.get<OrganizationDto>(`${ORG_BASE}${id}/`);
  return data;
}

export async function updateOrganization(
  id: number,
  input: Partial<OrganizationUpdateInput>,
): Promise<OrganizationDto> {
  const { data } = await apiClient.patch<OrganizationDto>(`${ORG_BASE}${id}/`, input);
  return data;
}

/** Upload a new logo via multipart. The backend treats it like any other PATCH. */
export async function uploadOrganizationLogo(
  id: number,
  file: File,
): Promise<OrganizationDto> {
  const form = new FormData();
  form.append('logo', file);
  const { data } = await apiClient.patch<OrganizationDto>(`${ORG_BASE}${id}/`, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

// ---- Company bank accounts (org receiving accounts, printed on AR invoices) ----
const BANK_BASE = '/organizations/organization-bank-accounts/';

/** Mirrors CompanyBankAccountReadSerializer. */
export interface CompanyBankAccount {
  id: number;
  public_id: string;
  label: string;
  account_holder: string;
  bank_name: string;
  bank_address: string;
  iban: string;
  swift_bic: string;
  account_number: string;
  routing_number: string;
  account_type: string;
  currency: string;
  country: string;
  is_default: boolean;
  notes: string;
  created: string;
  modified: string;
}

/** Write payload — matches CompanyBankAccountWriteSerializer (all optional/blank). */
export type CompanyBankAccountInput = Partial<
  Omit<CompanyBankAccount, 'id' | 'public_id' | 'created' | 'modified'>
>;

export async function listCompanyBankAccounts(): Promise<CompanyBankAccount[]> {
  const { data } = await apiClient.get<Paginated<CompanyBankAccount>>(BANK_BASE);
  return data.results;
}

export async function createCompanyBankAccount(
  input: CompanyBankAccountInput,
): Promise<CompanyBankAccount> {
  const { data } = await apiClient.post<CompanyBankAccount>(BANK_BASE, input);
  return data;
}

export async function updateCompanyBankAccount(
  publicId: string,
  input: CompanyBankAccountInput,
): Promise<CompanyBankAccount> {
  const { data } = await apiClient.patch<CompanyBankAccount>(`${BANK_BASE}${publicId}/`, input);
  return data;
}

export async function deleteCompanyBankAccount(publicId: string): Promise<void> {
  await apiClient.delete(`${BANK_BASE}${publicId}/`);
}

// ---- Audit log ----
export interface AuditLogEntry {
  id: number;
  action: string;
  actor: number | null;
  actor_name: string | null;
  content_type: number | null;
  object_id: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  created: string;
}

export interface AuditLogParams {
  page?: number;
  search?: string;
  action?: string;
  actor?: number;
}

export async function listAuditLogs(params: AuditLogParams = {}): Promise<Paginated<AuditLogEntry>> {
  const { data } = await apiClient.get<Paginated<AuditLogEntry>>('/audit/logs/', { params });
  return data;
}

// ---- Service bots & API keys (ADR-081) ----
const BOTS_BASE = '/organizations/bots/';

/** A bot or personal access token + its key. Mirrors `BotKeyReadSerializer`. */
export interface Bot {
  /** Full key id (`prefix.hashed`) — opaque. Use `prefix` for actions. */
  id: string;
  /** Non-secret key prefix; the URL key for rotate/revoke/set-roles. */
  prefix: string;
  name: string;
  /** "bot": synthetic service account. "user": PAT acting as a real person. */
  kind: 'bot' | 'user';
  /** Display name of who the key acts as (bot name or the user's full name). */
  bot_name: string | null;
  roles: string[];
  revoked: boolean;
  last_used_at: string | null;
  created: string;
}

/** Create/rotate response — carries the raw key exactly once. */
export type BotWithKey = Bot & { api_key: string };

export async function listBots(): Promise<Bot[]> {
  const { data } = await apiClient.get<Paginated<Bot>>(BOTS_BASE);
  return data.results;
}

export async function createBot(input: {
  name: string;
  /** Bot mode: roles for a new synthetic service account. */
  role_ids?: number[];
  /** PAT mode: bind to an existing user — acts as them with their own roles. */
  user_id?: number;
}): Promise<BotWithKey> {
  const { data } = await apiClient.post<BotWithKey>(BOTS_BASE, input);
  return data;
}

export async function setBotRoles(prefix: string, roleIds: number[]): Promise<Bot> {
  const { data } = await apiClient.patch<Bot>(`${BOTS_BASE}${prefix}/`, { role_ids: roleIds });
  return data;
}

export async function rotateBotKey(prefix: string): Promise<BotWithKey> {
  const { data } = await apiClient.post<BotWithKey>(`${BOTS_BASE}${prefix}/rotate/`);
  return data;
}

export async function revokeBot(prefix: string): Promise<void> {
  await apiClient.delete(`${BOTS_BASE}${prefix}/`);
}

// ---- Integration status ----
export interface IntegrationStatus {
  key: string;
  label: string;
  category: string;
  configured: boolean;
}

export async function getIntegrationStatus(): Promise<IntegrationStatus[]> {
  const { data } = await apiClient.get<{ results: IntegrationStatus[] }>('/integrations/status/');
  return data.results;
}
